/**
 * Site detail (`/origins/:siteUuid`) — the site-scoped landing page.
 *
 * Replacement for legacy `SiteDetailIsland.tsx`. Path moved under
 * `/origins/*` per topology.md §2. Features per `05-pages.md` §5b:
 *   - Site metadata card (name, URL, slug, team).
 *   - Overview stats — total runs, success rate, averages.
 *   - Pages list (clickable rows) linking to
 *     `/origins/:siteUuid/pages/:pageUuid` (the timeline).
 *   - "Monitor this site" action linking to `/monitors/new?siteUuid=…`.
 *
 * Monitor settings do NOT live here — they've moved to the Monitors
 * surface so there's one canonical place to manage schedules.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { SiteFavicon } from '@/components/primitives/SiteFavicon';
import { UsageCard } from '@/components/primitives/UsageCard';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import { FetchError } from '@/lib/api';
import type { SiteOverview } from '@/lib/api-types';
import { toastSuccess } from '@/lib/errors';
import { hostOf, relativeTime } from '@/lib/format';
import {
  useDeleteSite,
  useMonitors,
  useOrigins,
  useSite,
  useSiteOverview,
  useSitePages,
} from '@/lib/queries';
import {
  ArrowSquareOut,
  Browsers,
  ChartLineUp,
  DotsThreeVertical,
  Globe,
  Pulse,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/origins/$siteUuid/')({
  component: SiteDetailPage,
});

function SiteDetailPage(): ReactElement {
  const { siteUuid } = Route.useParams();
  const navigate = useNavigate();

  const site = useSite(siteUuid);
  // "Fresh site" = no monitors AND no audit-derived pages. Pages get
  // created the first time any URL under this origin is audited
  // (including one-off `clickweave audits create` calls), so a site
  // with pages but no monitors should render metrics, not the setup
  // card. We always fetch `pages` now — it's a cheap management
  // endpoint — so the heuristic has the data it needs to decide.
  const monitors = useMonitors();
  const hasMonitors = Boolean(
    monitors.data?.some((m) => m.site_uuid === siteUuid && m.monitoring_enabled),
  );
  const pages = useSitePages(siteUuid);
  const hasPages = (pages.data?.length ?? 0) > 0;
  const setupReady = !monitors.isLoading && !pages.isLoading;
  const isFreshSite = setupReady && !hasMonitors && !hasPages;
  // Overview is gated on "has any data" — either a monitor or
  // recorded audits. Auto-created sites land here too once their
  // first audit completes and pages backfill.
  const overview = useSiteOverview(siteUuid, undefined, { enabled: hasMonitors || hasPages });

  // Fall back to the origins list query's cached row while `useSite`
  // (management endpoint) loads — lets us paint the header sooner.
  const listFallback = useOrigins({ limit: 200 });
  const listSite = listFallback.data?.sites.find((s) => s.uuid === siteUuid);
  const siteData = site.data ?? listSite ?? null;

  // Delete flow — tucked into the kebab menu so the destructive
  // action doesn't compete visually with "Visit" / "Monitor".
  const deleteSite = useDeleteSite();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const onDelete = async (): Promise<void> => {
    await deleteSite.mutateAsync({ uuid: siteUuid });
    setDeleteOpen(false);
    toastSuccess('Origin deleted', siteData?.name ?? undefined);
    await navigate({ to: '/origins' });
  };

  const breadcrumb = siteData
    ? [
        { label: 'Origins', href: '/origins' },
        { label: hostOf(siteData.url) },
        { label: siteData.name },
      ]
    : [{ label: 'Origins', href: '/origins' }, { label: siteUuid }];

  return (
    <Island
      breadcrumb={breadcrumb}
      actions={
        <PageActions>
          {siteData && (
            <Button
              variant="outline"
              // biome-ignore lint/a11y/useAnchorContent: content lives in the Button children which Base UI merges into the rendered <a>
              render={<a href={siteData.url} target="_blank" rel="noreferrer noopener" />}
            >
              <ArrowSquareOut aria-hidden="true" />
              Visit
            </Button>
          )}
          <Button render={<Link to="/monitors/new" search={{ siteUuid }} />}>
            <Pulse aria-hidden="true" />
            Monitor this site
          </Button>
          <Menu>
            <MenuTrigger render={<Button size="icon" variant="ghost" aria-label="More actions" />}>
              <DotsThreeVertical aria-hidden="true" />
            </MenuTrigger>
            <MenuPopup align="end">
              <MenuItem
                onClick={() => {
                  setDeleteOpen(true);
                }}
              >
                <Trash aria-hidden="true" />
                Delete origin…
              </MenuItem>
            </MenuPopup>
          </Menu>
        </PageActions>
      }
    >
      {site.isError && !listSite && (
        <EmptyState
          icon={Warning}
          title="Couldn't load this site"
          description={
            site.error instanceof FetchError
              ? site.error.message
              : 'Try refreshing, or go back to the origins list.'
          }
          action={
            <Button variant="outline" render={<Link to="/origins" />}>
              Back to origins
            </Button>
          }
        />
      )}

      {!site.isError && (
        <div className="space-y-6">
          <SiteMetadataCard
            name={siteData?.name ?? siteUuid}
            url={siteData?.url ?? ''}
            slug={siteData?.slug ?? ''}
            loading={!siteData}
          />

          {/* Fresh site (no monitors yet) — render the setup card
              instead of the metrics widgets, which would otherwise
              query a Tinybird datasource that's empty / unconfigured
              and surface a vendor-named 503 on a customer page. */}
          {isFreshSite && <SetupCard siteUuid={siteUuid} />}

          {!isFreshSite && setupReady && (
            <OverviewGrid overview={overview.data} loading={overview.isLoading} />
          )}

          {!isFreshSite && setupReady && (
            <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
              <header className="flex items-center justify-between border-b border-[var(--color-line-soft)] px-4 py-3">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--color-ink)]">Pages</h3>
                  <p className="text-xs text-[var(--color-mute)]">
                    Drill into a page to see its timeline and recent audits.
                  </p>
                </div>
              </header>
              <div className="p-2">
                {pages.isLoading && (
                  <div className="space-y-2 p-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                )}
                {pages.isError && !pages.isLoading && (
                  <div className="p-2">
                    <ErrorState
                      title="Couldn't load pages"
                      error={pages.error}
                      retry={() => {
                        void pages.refetch();
                      }}
                    />
                  </div>
                )}
                {!pages.isLoading && !pages.isError && pages.data && pages.data.length === 0 && (
                  <EmptyState
                    icon={Browsers}
                    title="No pages tracked yet"
                    description="Pages get added the first time a monitor or manual audit runs against a URL under this origin."
                  />
                )}
                {!pages.isLoading && !pages.isError && pages.data && pages.data.length > 0 && (
                  <ul className="divide-y divide-[var(--color-line-soft)]">
                    {pages.data.map((p) => (
                      <li key={p.uuid}>
                        <Link
                          to="/origins/$siteUuid/pages/$pageUuid"
                          params={{ siteUuid, pageUuid: p.uuid }}
                          className="flex items-center justify-between gap-4 px-3 py-3 transition-colors hover:bg-[var(--color-paper-alt)]/40"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-[var(--color-ink)]">
                              {p.name}
                            </div>
                            <div className="truncate text-xs text-[var(--color-mute)]">{p.url}</div>
                          </div>
                          <span className="text-xs text-[var(--color-mute)]">
                            {p.enabled ? 'active' : 'paused'}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
        </div>
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this origin?"
        description={
          <>
            <strong>{siteData?.name ?? 'This origin'}</strong> and all of its monitors, pages, and
            audit history will be removed. This can't be undone.
          </>
        }
        confirmLabel="Delete origin"
        destructive
        loading={deleteSite.isPending}
        onConfirm={onDelete}
      />
    </Island>
  );
}

/**
 * Setup card for a site that has no monitors yet (and, in the
 * future, no RUM beacons). Renders two side-by-side paths to first
 * data — synthetic via `/monitors/new` and RUM via the pixel
 * snippet. The pixel side is intentionally a "Coming soon"
 * placeholder for now: `plans/1.0.0/pixel-ingest/` will light it up.
 *
 * The card replaces the metrics OverviewGrid + Pages section
 * entirely on fresh sites — there's nothing useful to show in those
 * widgets until one of these two paths is configured, and surfacing
 * an empty grid + 503 toast was the original UX bug this fix
 * addresses.
 */
function SetupCard({ siteUuid }: { siteUuid: string }): ReactElement {
  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-6">
      <header className="mb-5 space-y-1">
        <h3 className="text-base font-semibold text-[var(--color-ink)]">Start collecting data</h3>
        <p className="text-sm text-[var(--color-mute)]">
          Clickweave has two ways to measure this site. Pick either to start — most teams end up
          running both side by side.
        </p>
      </header>
      <div className="grid gap-4 sm:grid-cols-2">
        <SetupOption
          icon={ChartLineUp}
          title="Synthetic"
          subtitle="Lighthouse audits we run for you"
          body="Schedule audits across regions and device profiles. No code to install — Clickweave drives a real Chrome from the regions you pick."
          cta={
            <Button render={<Link to="/monitors/new" search={{ siteUuid }} />}>
              Add a monitor
            </Button>
          }
        />
        <SetupOption
          icon={Globe}
          title="Real-user (RUM)"
          subtitle="From your visitors' browsers"
          body="A 5 KB script on your site reports the Core Web Vitals your real visitors experience. Privacy-first — no PII, no cookies."
          badge="Coming soon"
          cta={
            <Button variant="outline" disabled>
              Install the pixel
            </Button>
          }
        />
      </div>
    </section>
  );
}

function SetupOption({
  icon: Icon,
  title,
  subtitle,
  body,
  badge,
  cta,
}: {
  icon: typeof ChartLineUp;
  title: string;
  subtitle: string;
  body: string;
  badge?: string;
  cta: ReactElement;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/40 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-accent-soft)] text-[var(--color-accent-brand)]">
          <Icon aria-hidden="true" weight="duotone" size={20} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h4>
            {badge && (
              <span className="rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-mute)]">
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--color-mute)]">{subtitle}</p>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-[var(--color-ink-soft)]">{body}</p>
      <div className="mt-auto">{cta}</div>
    </div>
  );
}

function SiteMetadataCard({
  name,
  url,
  slug,
  loading,
}: {
  name: string;
  url: string;
  slug: string;
  loading: boolean;
}): ReactElement {
  if (loading) return <Skeleton className="h-24 w-full" />;
  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-start gap-3">
        <SiteFavicon url={url} size={50} className="rounded-md" />
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-[var(--color-ink)]">{name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[var(--color-mute)]">
            <span className="truncate">{url}</span>
            <span className="rounded-sm bg-[var(--color-paper-alt)] px-2 py-0.5 font-mono text-xs text-[var(--color-ink-soft)]">
              {slug}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function OverviewGrid({
  overview,
  loading,
}: {
  overview: SiteOverview | undefined;
  loading: boolean;
}): ReactElement {
  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-line)] bg-[var(--color-paper-alt)]/30 p-6 text-center text-sm text-[var(--color-mute)]">
        No audit data yet for this site.
      </div>
    );
  }

  const successRate =
    overview.total_runs > 0
      ? Math.round((overview.success_runs / overview.total_runs) * 100)
      : null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <UsageCard title="Total runs" value={overview.total_runs} />
      <UsageCard
        title="Success rate"
        value={successRate == null ? '—' : `${successRate}%`}
        hint={overview.last_run_at ? `Last run ${relativeTime(overview.last_run_at)}` : undefined}
      />
      <UsageCard
        title="Avg performance"
        value={
          overview.avg_performance_score == null ? '—' : Math.round(overview.avg_performance_score)
        }
      />
      <UsageCard
        title="Error runs"
        value={overview.error_runs}
        hint={overview.error_runs > 0 ? 'Investigate failing monitors.' : 'All green.'}
      />
    </div>
  );
}
